import type { IUserIdentityVerificationV2Response } from '@/api/types';
import { getClientSideUserAgent, isMobile } from '@vuddy/utils';
import webViewService from './webViewService';

export interface IdentityVerificationV2LauncherPayload {
  version: 'v2';
  traceId: string;
  callUrl: string;
  regCertKey: string;
  kcpPageSubmitYn: string;
}

const createIdentityVerificationLaunchId = () =>
  window.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const createIdentityVerificationResultRedirectUrl = () =>
  '/identity-verification/result';

export const createIdentityVerificationStartUrl = ({
  launchId = createIdentityVerificationLaunchId(),
  returnUrl,
}: {
  launchId?: string;
  returnUrl: string;
}) =>
  `/identity-verification?__request=1&return_url=${encodeURIComponent(returnUrl)}&launch_id=${encodeURIComponent(launchId)}`;

export const createIdentityVerificationV2LauncherPayload = (
  response: IUserIdentityVerificationV2Response
): IdentityVerificationV2LauncherPayload => ({
  version: 'v2',
  traceId: response.traceId,
  callUrl: response.callUrl,
  regCertKey: response.regCertKey,
  kcpPageSubmitYn: response.kcpPageSubmitYn,
});

export const isIdentityVerificationV2LauncherPayload = (
  payload: unknown
): payload is IdentityVerificationV2LauncherPayload => {
  if (!payload || typeof payload !== 'object') return false;

  const value = payload as Record<string, unknown>;
  return (
    value.version === 'v2' &&
    typeof value.traceId === 'string' &&
    typeof value.callUrl === 'string' &&
    typeof value.regCertKey === 'string' &&
    typeof value.kcpPageSubmitYn === 'string'
  );
};

const shouldUseKcpPageTransition = (userAgent: string) =>
  webViewService.isAppWebView(userAgent) || isMobile(userAgent);

export const getIdentityVerificationV2FormConfig = ({
  payload,
  userAgent = getClientSideUserAgent(),
}: {
  payload: IdentityVerificationV2LauncherPayload;
  userAgent?: string;
}) => ({
  action: payload.callUrl,
  fields: {
    reg_cert_key: payload.regCertKey,
    kcp_page_submit_yn: shouldUseKcpPageTransition(userAgent)
      ? 'Y'
      : payload.kcpPageSubmitYn || 'N',
  },
});
